import {
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../../_lib/epocus-palette";
import {
  SettingsCard,
  SettingsCardLabel,
} from "../../../settings/_lib/settings-card";
import { useEpocusProgramConfigQuery } from "../../../settings/program/_lib/__generated__/program-config-operations.gql";
import { ProgramSetupCard } from "../../../settings/program/_lib/program-setup-card";
import { useEpocusAdminProgramMembersQuery } from "../../_lib/__generated__/epocus-admin-operations.gql";
import { AdminPageHeader } from "../../_lib/admin-page-header";
import { AdminLoadingRow, AdminPageRoot } from "../../_lib/admin-page-root";
import { AdminSectionHeading } from "../../_lib/admin-section-heading";
import { AdminSeedDemoDataCard } from "../../_lib/admin-seed-demo-data-card";
import { ProgramMembersList } from "../../_lib/program-members-list";
import { useAdminProgram } from "../../_lib/use-admin-program";
import {
  linkToEpocusAdmin,
  linkToEpocusAdminAddProgramUser,
} from "../../links";

const AeTitleValue = styled(Typography)({
  fontSize: rem(20),
  fontWeight: 700,
  fontFamily: "monospace",
  color: EPOCUS_TEXT_PRIMARY,
  letterSpacing: "0.06em",
});

const BACK_BUTTON_STYLE = {
  textTransform: "none",
  padding: "10px 12px",
  marginLeft: -12,
  minHeight: 44,
  alignSelf: "flex-start",
} as const;

const MutedBody = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const FacilityValue = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

export const EpocusAdminProgramPage: FC = () => {
  const { programId } = useParams();
  const {
    program,
    loading: programLoading,
    error: programError,
    notFound,
  } = useAdminProgram(programId);

  const { data, loading, error, refetch } = useEpocusAdminProgramMembersQuery({
    variables: { programId: programId ?? "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const { data: configData } = useEpocusProgramConfigQuery({
    variables: { programId: programId ?? "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  if (!programId) return <Navigate to={linkToEpocusAdmin()} replace />;

  const members = data?.epocusProgramMembers;

  const backButton = (
    <Button component={Link} to={linkToEpocusAdmin()} style={BACK_BUTTON_STYLE}>
      ← Back to programs
    </Button>
  );

  if (programLoading) {
    return (
      <AdminPageRoot>
        {backButton}
        <AdminLoadingRow>
          <CircularProgress aria-label="Loading program" />
        </AdminLoadingRow>
      </AdminPageRoot>
    );
  }

  if (programError || notFound) {
    return (
      <AdminPageRoot>
        {backButton}
        <SettingsCard>
          <MutedBody role="alert">
            {programError
              ? "Couldn't load this program."
              : "That program doesn't exist."}
          </MutedBody>
        </SettingsCard>
      </AdminPageRoot>
    );
  }

  return (
    <AdminPageRoot>
      {backButton}

      <AdminPageHeader title={program?.name ?? "Program"} />

      {program?.dimseAeTitle && (
        <SettingsCard>
          <SettingsCardLabel>Program AE title</SettingsCardLabel>
          <AeTitleValue>{program.dimseAeTitle}</AeTitleValue>
          <MutedBody>
            Set this as the destination AE title when configuring a POCUS device
            to send studies to this program.
          </MutedBody>
        </SettingsCard>
      )}

      {program?.elephantFacilityId && (
        <SettingsCard>
          <SettingsCardLabel>Linked ElephantOS facility</SettingsCardLabel>
          <FacilityValue>{program.elephantFacilityId}</FacilityValue>
        </SettingsCard>
      )}

      <ProgramSetupCard
        programId={programId}
        config={configData?.programConfig}
      />

      <AdminSectionHeading>Demo data</AdminSectionHeading>
      <AdminSeedDemoDataCard programId={programId} />

      <AdminSectionHeading>Members</AdminSectionHeading>
      {members ? (
        <>
          {error && (
            <SettingsCard>
              <MutedBody role="alert">
                Couldn't refresh members — showing the last loaded list.
              </MutedBody>
            </SettingsCard>
          )}
          {members.length === 0 ? (
            <SettingsCard>
              <MutedBody>No one has been added to this program yet.</MutedBody>
            </SettingsCard>
          ) : (
            <ProgramMembersList members={members} />
          )}
        </>
      ) : loading ? (
        <AdminLoadingRow>
          <CircularProgress aria-label="Loading members" />
        </AdminLoadingRow>
      ) : (
        <SettingsCard>
          <MutedBody role="alert">Couldn't load members.</MutedBody>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => void refetch()}
            style={{ alignSelf: "flex-start", minHeight: 44 }}
          >
            Try again
          </Button>
        </SettingsCard>
      )}

      <Button
        variant="contained"
        color="primary"
        component={Link}
        to={linkToEpocusAdminAddProgramUser(programId)}
        style={{ alignSelf: "flex-start", minHeight: 44 }}
      >
        Add user
      </Button>
    </AdminPageRoot>
  );
};
